import React, { type ErrorInfo } from 'react';
import { logger } from '../../../api/logging-api';
import { TechnicalError } from 'individual/views/TechnicalError';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

let stateSetter: ((state: ErrorBoundaryState) => void) | undefined;

export function setErrorState(hasError: boolean) {
  stateSetter?.({ hasError });
}

// eslint-disable-next-line import/no-named-as-default-member
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static getDerivedStateFromError = () => {
    // Update state to indicate an error has occurred
    return { hasError: true };
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  componentDidMount(): void {
    stateSetter = this.setState.bind(this);
  }

  componentWillUnmount() {
    stateSetter = undefined;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service
    logger.logError(error);
    this.setState({ hasError: true, error, errorInfo });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <TechnicalError />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
